import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { markDateInteraction } from '../../lib/activityLogQueryParam';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

const ADDON_COLORS   = ['#36A2EB','#FF6384','#4BC0C0','#FFCE56','#9966FF','#FF9F40','#22c55e','#f97316'];
const ADDON_BG       = ['#eff8ff','#fff0f3','#f0fdfd','#fffdf0','#f5f0ff','#fff7ef','#f0fdf4','#fff7ed'];
const ADDON_TEXT     = ['#1d6fa4','#e11d48','#0f766e','#92700a','#6d28d9','#c2410c','#15803d','#c2410c'];

const getFirstDayOfCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};
const getCurrentDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const getFirstDayOfPreviousMonth = () => {
  const now = new Date();
  now.setMonth(now.getMonth() - 1);
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};
const getLastDayOfPreviousMonth = () => {
  const now = new Date();
  now.setDate(0);
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDateForAPI = (d, isEnd) =>
  d ? (isEnd ? `${d} 23:59:59.999` : `${d} 00:00:00.000`) : '';

const periodLabels = { current: 'Current Month', previous: 'Previous Month', custom: 'Custom Month' };

// ── Skeleton ──────────────────────────────────────────────────────────────────
const SkeletonView = () => (
  <Stack gap={1.5} mt={1}>
    {/* Total chip skeleton */}
    <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: '10px', mb: 0.5 }} />
    <Stack direction="row" gap={2} pb={0.5}>
      {[24, '25%', 72].map((w, i) => (
        <Skeleton key={i} variant="text" width={w} height={14} sx={{ flexShrink: i === 2 ? 0 : 'unset' }} />
      ))}
    </Stack>
    {[1, 2, 3, 4, 5].map(k => (
      <Stack key={k} direction="row" gap={2} alignItems="center">
        <Skeleton variant="text" width={20} height={14} sx={{ flexShrink: 0 }} />
        <Skeleton variant="text" width="30%" height={14} sx={{ flexShrink: 0 }} />
        <Skeleton variant="rectangular" width={88} height={22} sx={{ borderRadius: '6px', flexShrink: 0 }} />
      </Stack>
    ))}
  </Stack>
);

// ── Empty / Error ─────────────────────────────────────────────────────────────
const EmptyView = ({ isError, onRetry }) => (
  <Stack alignItems="center" justifyContent="center" gap={2} py={5}>
    <Box sx={{ width: 64, height: 64, borderRadius: '50%', background: isError ? '#fff1f2' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
        {isError ? (
          <>
            <path d="M12 9v4M12 17h.01" stroke="#e11d48" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#e11d48" strokeWidth="1.5" strokeLinejoin="round" />
          </>
        ) : (
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#475569', mb: 0.5 }}>
        {isError ? 'Failed to Load Data' : 'No Add-on Data Available'}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: '#94a3b8', maxWidth: 300 }}>
        {isError
          ? 'There was an error fetching add-on data. Please try again.'
          : 'No add-on usage found for the selected date range. Try switching to a different period.'}
      </Typography>
    </Box>
    {isError && (
      <Button
        variant="outlined"
        size="small"
        onClick={onRetry}
        sx={{ textTransform: 'none', borderColor: '#e11d48', color: '#e11d48', '&:hover': { borderColor: '#be123c', background: '#fff1f2' } }}
      >
        Retry
      </Button>
    )}
  </Stack>
);

// ── Main Component ────────────────────────────────────────────────────────────
const SmartReportAddonsTable = ({ onStatsChange }) => {
  const [tableData, setTableData]       = useState([]);
  const [rawAddons, setRawAddons]       = useState([]);
  const [totalReports, setTotalReports]   = useState(null); // real total from smartreport counts API
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [startDate, setStartDate]       = useState(getFirstDayOfCurrentMonth);
  const [endDate, setEndDate]           = useState(getCurrentDate);
  const [filterType, setFilterType]     = useState('current');
  const [showDialog, setShowDialog]     = useState(false);
  const [reRender, setReRender]         = useState(false);

  // Re-fetch when auth token refreshes (storage event)
  useEffect(() => {
    const handler = () => setReRender(p => !p);
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const fetchAddons = async (start, end) => {
    setLoading(true);
    setError(null);
    try {
      const idToken =
        localStorage.getItem('idTokenBilling') ||
        JSON.parse(localStorage.getItem('loginDetails'))?.idToken ||
        '';
      if (!idToken) {
        setError('api_error'); setLoading(false); return;
      }

      const formattedStart = formatDateForAPI(start, false);
      const formattedEnd = formatDateForAPI(end, true);
      const url = `https://api.niroggyan.com/billing/smartreport/counts?query_by=range&start=${encodeURIComponent(formattedStart)}&end=${encodeURIComponent(formattedEnd)}&query_on=addons`;
      const requestConfig = { method: 'GET', url, headers: { Authorization: idToken } };

      const payloadSent = { query_by: 'range', start: formattedStart, end: formattedEnd, query_on: 'addons' };
      console.log('=== Smart Report Add-ons ===');
      console.log('1. API Calling:', url.split('?')[0]); // Log base URL
      console.log('2. Payload Sending (Query Params):', payloadSent);

      const res = await axios(requestConfig);

      console.log('3. Response Getting:', res.data);
      console.log('============================');

      if (res.data.isSuccess && res.data.data?.length) {
        setRawAddons(res.data.data);
      } else {
        setRawAddons([]);
        setTableData([]);
        setError('no_data');
      }
    } catch (err) {
      setRawAddons([]);
      setError('api_error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch real total smartreport count (same source as Monthly Chart)
  const fetchTotalReports = async (start, end) => {
    try {
      const idToken =
        localStorage.getItem('idTokenBilling') ||
        JSON.parse(localStorage.getItem('loginDetails'))?.idToken ||
        '';
      if (!idToken) return;
      const formattedStart = formatDateForAPI(start, false);
      const formattedEnd   = formatDateForAPI(end, true);
      const url = `https://api.niroggyan.com/billing/smartreport/counts?query_on=counts&query_by=range&start=${encodeURIComponent(formattedStart)}&end=${encodeURIComponent(formattedEnd)}`;
      const res = await axios({ method: 'GET', url, headers: { Authorization: idToken } });
      if (res.data?.data) {
        const total = res.data.data.reduce((s, item) => s + parseInt(item.count || 0), 0);
        setTotalReports(total);
      }
    } catch (err) {
      // silently fail — chip just won't show
    }
  };

  // Notify parent when total or date range changes (for header chip)
  useEffect(() => {
    if (onStatsChange) {
      onStatsChange({ total: totalReports, startDate, endDate });
    }
  }, [totalReports, startDate, endDate]);

  useEffect(() => {
    if (rawAddons.length > 0 && totalReports !== null) {
      const rows = rawAddons
        .map((item, i) => {
          const count = parseInt(item.usage_count || item.count || 0);
          const rawName = item.addon_name || item.addon || item.name || `Addon ${i + 1}`;
          const label   = rawName.charAt(0).toUpperCase() + rawName.slice(1).replace(/_/g, ' ');
          const percentage = totalReports > 0 ? ((count / totalReports) * 100).toFixed(1) : '0';
          return {
            label,
            count,
            percentage,
            color:     ADDON_COLORS[i % ADDON_COLORS.length],
            bg:        ADDON_BG[i % ADDON_BG.length],
            textColor: ADDON_TEXT[i % ADDON_TEXT.length],
          };
        })
        .sort((a, b) => b.count - a.count);
      setTableData(rows);
    } else if (rawAddons.length === 0) {
      setTableData([]);
    }
  }, [rawAddons, totalReports]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchAddons(startDate, endDate);
      fetchTotalReports(startDate, endDate);
    }
  }, [startDate, endDate, reRender]);

  const handleCurrentMonth  = () => { markDateInteraction(); setStartDate(getFirstDayOfCurrentMonth()); setEndDate(getCurrentDate()); setFilterType('current'); };
  const handlePreviousMonth = () => { markDateInteraction(); setStartDate(getFirstDayOfPreviousMonth()); setEndDate(getLastDayOfPreviousMonth()); setFilterType('previous'); };
  const applyCustomDates    = () => { markDateInteraction(); setShowDialog(false); setFilterType('custom'); };

  return (
    <Box sx={{ p: '0 20px 20px' }}>
      {/* ── Custom Date Dialog ── */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '15px' }}>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            <Box>
              <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 0.5 }}>Start Date</Typography>
              <input
                className="customDateTag"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={{ width: '100%' }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 0.5 }}>End Date</Typography>
              <input
                className="customDateTag"
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={{ width: '100%' }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            size="small"
            onClick={applyCustomDates}
            sx={{ textTransform: 'none', background: '#3567D6', '&:hover': { background: '#2555c0' } }}
          >
            Apply
          </Button>
          <Button variant="outlined" size="small" onClick={() => setShowDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Note + Period buttons ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" gap={1}>
        {/* Note on the left */}
        <Stack direction="row" alignItems="center" gap={0.6}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" stroke="#3567D6" strokeWidth="1.8" />
            <path d="M12 8h.01M12 11v5" stroke="#3567D6" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <Typography sx={{ fontSize: '11px', color: '#3567D6' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>Note:&nbsp;</Box>
            Counts are calculated starting from 26th Mar 2026 onwards.
          </Typography>
        </Stack>

        {/* Period toggle buttons on the right */}
        <Stack direction="row" gap={0.75} sx={{ background: '#F0F4FF', borderRadius: '10px', p: '4px' }}>
          {['current', 'previous', 'custom'].map(key => (
            <Box
              key={key}
              onClick={() => {
                if (key === 'custom') setShowDialog(true);
                else if (key === 'current') handleCurrentMonth();
                else handlePreviousMonth();
              }}
              sx={{
                px: 1.5, py: 0.6, borderRadius: '7px', cursor: 'pointer',
                background: filterType === key ? '#3567D6' : 'transparent',
                transition: 'background 0.18s ease',
              }}
            >
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: filterType === key ? '#fff' : '#8897C4', whiteSpace: 'nowrap', transition: 'color 0.18s ease' }}>
                {periodLabels[key]}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>

      {/* ── Body ── */}
      {loading ? (
        <SkeletonView />
      ) : error && error !== 'no_data' ? (
        <EmptyView isError onRetry={() => fetchAddons(startDate, endDate)} />
      ) : tableData.length === 0 ? (
        <EmptyView isError={false} />
      ) : (
        <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['#', 'Add-on', 'Count & Share'].map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      fontSize: '11px', fontWeight: 600, color: '#94a3b8',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      borderBottom: '1px solid #e2e8f0', pb: 1.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((row, i) => (
                <TableRow key={i} sx={{ '&:last-child td': { border: 0 }, '&:hover': { background: '#f8fafc' } }}>
                  <TableCell sx={{ color: '#94a3b8', fontSize: '12px', width: 28, py: 1.5 }}>{i + 1}</TableCell>
                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#1e293b', py: 1.5 }}>{row.label}</TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        background: row.bg, color: row.textColor,
                        fontWeight: 600, fontSize: '12px',
                        borderRadius: '6px', px: 1.25, py: 0.5, whiteSpace: 'nowrap',
                      }}
                    >
                      {row.count.toLocaleString('en-IN')}
                      <Box component="span" sx={{
                        opacity: 0.75, fontWeight: 500, fontSize: '11px',
                        background: 'rgba(0,0,0,0.07)', borderRadius: '4px',
                        px: 0.7, py: 0.2,
                      }}>{row.percentage}%</Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}


    </Box>
  );
};

export default SmartReportAddonsTable;
